import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { themeVars, useTheme } from '../theme/theme';
import { useT } from '../lib/i18n';
import { useConnection } from '../lib/ride';
import { isOpen } from '../lib/rideScreen';
import { Icon } from './Icon';

export type ScreenName =
  | 'welcome' | 'phone-number' | 'code' | 'home' | 'search' | 'choose-ride' | 'confirm-pickup' | 'finding-a-driver'
  | 'driver-arriving' | 'on-the-trip' | 'arrived-pay-rate' | 'your-trips' | 'account';

const MAP_SCREENS = new Set<ScreenName>(['choose-ride', 'confirm-pickup', 'finding-a-driver', 'driver-arriving', 'on-the-trip']);

/** A full-height phone screen with the theme tokens for its surface (map screens use the map surface). */
/**
 * `kind` — how it sits on a tablet/desktop: `panel` (the left panel next to the shared map), `map` (over the map;
 * its MapSheet is the panel) or `page` (the whole stage). Phones ignore it.
 */
export function Screen({ name, children, style, label, kind }: { name: ScreenName; children: ReactNode; style?: CSSProperties; label?: string; kind?: 'panel' | 'map' | 'page' }) {
  const { mode } = useTheme();
  const isMap = MAP_SCREENS.has(name);
  return (
    <main className="zr-screen" data-screen={name} data-kind={kind ?? (isMap ? 'map' : 'panel')} data-theme={mode} aria-label={label} style={{ ...themeVars(mode, isMap ? 'map' : 'base'), ...style }}>
      {children}
      <ConnectionBanner />
    </main>
  );
}

/**
 * Offline: a banner with Retry (the screen keeps the last update). Socket down but REST fine during a ride: a
 * small "live updates paused" pill — the app is polling every few seconds, so the screen stays right.
 */
function ConnectionBanner() {
  const c = useConnection();
  const t = useT();
  const [busy, setBusy] = useState(false);
  const riding = !!c?.liveRide && isOpen(c.liveRide.status);
  const down = !!c && riding && !c.connected;
  // only after the socket has been down a few seconds (not while it is first connecting)
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (!down) return setPaused(false);
    const tm = window.setTimeout(() => setPaused(true), 4000);
    return () => window.clearTimeout(tm);
  }, [down]);
  if (!c) return null;
  if (c.offline)
    return (
      <div role="alert" data-testid="offline-banner" style={{ position: 'absolute', left: 16, right: 16, top: 'calc(var(--sat) + 60px)', zIndex: 55, padding: '10px 8px 10px 14px', borderRadius: 18, background: 'var(--float, var(--s1))', boxShadow: 'var(--shadow, 0 8px 24px rgba(0,0,0,0.3))', display: 'flex', alignItems: 'center', gap: 12, color: 'var(--ink)' }}>
        <span aria-hidden="true" style={{ flex: 'none', width: 34, height: 34, borderRadius: 11, background: 'var(--dangerSoft)', color: 'var(--danger)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon name="alert" size={18} sw={2.2} />
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 14, lineHeight: '19px', fontWeight: 700 }}>{t('net.offline')}</span>
          <span style={{ display: 'block', fontSize: 12.5, lineHeight: '17px', fontWeight: 500, color: 'var(--ink2)' }}>{t('net.offlineBody')}</span>
        </span>
        <button
          type="button"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            await c.retry();
            setBusy(false);
          }}
          style={{ flex: 'none', height: 40, padding: '0 14px', border: 0, borderRadius: 20, background: 'var(--pri)', color: 'var(--priInk)', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
        >
          {t('common.retry')}
        </button>
      </div>
    );
  if (paused && down)
    return (
      <div data-testid="live-paused" style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', top: 'calc(var(--sat) + 64px)', zIndex: 55, height: 28, padding: '0 12px', borderRadius: 14, background: 'var(--float, var(--s1))', boxShadow: 'var(--shadow)', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: 'var(--ink2)', whiteSpace: 'nowrap' }}>
        <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: 3, background: 'var(--ink3)' }} />
        {t('net.live')}
      </div>
    );
  return null;
}
